import React, { ChangeEvent, useRef } from 'react';
import {
    ModalHeader,
    ModalBody,
    FormGroup,
    Row,
    Col,
    Button,
} from 'reactstrap';
import { useEffect, useState } from 'react';
import createNotification from '../../../../../utils/createNotification';
import Helpers from '../../../../../utils/helper';
import { Field, Form, Input } from '@availity/form';
import * as yup from 'yup';
import ButtonLoader from '../../../../../components/Layout/Buttons/ButtonLoader';
import './AddDocumentTemplateModal.scss';
import Rodal from '../../../../../components/Rodal/Rodal';
import { useStateSelector } from '../../../../../store/selectors';
import { TemplateCategory } from '../../../../../slices/marketplace/timeline/models/enums/document-template-category.enum';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import SelectFileInputAvaility from '../../../../../components/SelectFileInputAvaility/SelectFileInputAvaility';
import { TemplateTypes } from '../../../../../models/SignDocumentModule/TemplateTypes';
import { PrefilledTags } from '../../../../../models/PrefilledTags/PrefilledTags';

interface AddDocumentTemplateModalProps {
    isOpened: boolean;
    onClose: () => void;
    afterSuccessfulAdd: () => void;
}
interface SignRequestTemplateContract {
    name: string;
    uuid: string;
    signersCount: number;
}

export interface TemplateContract {
    id: number;
    uuid: string;
    name: string;
    filePath: string;
    fileUrl: string;
    category: TemplateCategory;
    type: TemplateTypes;
    sidesCount: number;
    prefillTags: PrefilledTags[];
    // documentSectionReplacements: DocumentSectionReplacement[];
}

interface FormFileds {
    uuid: string;
    name: string;
    sourceFile: string;
    signersCount: number;
}

export const AddDocumentTemplateModal = (
    props: AddDocumentTemplateModalProps
) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [templates, setTemplates] = useState<SignRequestTemplateContract[]>(
        []
    );
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const modalForm = useRef(null);
    const [templateType, setTemplateType] = useState<TemplateTypes>(
        TemplateTypes.SignRequest
    );
    const [attachedFile, setAttachedFile] = useState<File>(null);
    const [isFileUpdated, setIsFileUpdated] = useState(false);

    useEffect(() => {
        if (props.isOpened) {
            setIsLoading(true);
            axios
                .get('/api/SignTemplates/SignRequest')
                .then((response) => {
                    if (response.status !== 200) {
                        createNotification(
                            'An unexpected error occurred during Sign Request Templates fetching',
                            'error'
                        );
                        return;
                    }
                    setTemplates(response.data);
                })
                .catch((error) => {
                    createNotification(
                        'An unexpected error occurred during Sign Request Templates fetching',
                        'error'
                    );
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [props.isOpened]);

    const saveTemplate = (fields: FormFileds) => {
        setIsSaving(true);
        let request = null;
        switch (templateType) {
            case TemplateTypes.SignRequest:
                request = createTemplateBySignRequest(fields);
                break;
            case TemplateTypes.CustomDocFile:
                request = createTemplateByDocument(
                    fields.name,
                    attachedFile.webkitRelativePath,
                    fields.signersCount
                );
                break;
            default:
                return;
        }

        request
            .then((response: any) => {
                if (response.status === 200) {
                    createNotification(
                        'Template has been successfully added.',
                        'success'
                    );
                    props.afterSuccessfulAdd();
                } else {
                    createNotification(
                        response.response?.data?.detail ??
                            'Unexpected error occurred while adding new Template.',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'Unexpected error occurred while adding new Template.',
                    'error'
                );
            })
            .finally(() => setIsSaving(false));
    };

    const createTemplateBySignRequest = (fields: any) => {
        return axios.post('api/SignTemplates', {
            uuid: fields.uuid,
            name: fields.name,
            category: TemplateCategory.Marketplace,
        });
    };

    const createTemplateByDocument = (
        templateName: string,
        filePath: string,
        signersCount: number
    ) => {
        const postObject: TemplateContract = {
            id: 0,
            uuid: '',
            name: templateName,
            filePath: filePath,
            fileUrl: '',
            category: TemplateCategory.Marketplace,
            type: TemplateTypes.CustomDocFile,
            sidesCount: signersCount,
            prefillTags: [],
        };

        const formData = new FormData();
        formData.append('File', attachedFile);
        formData.append('Data', JSON.stringify(postObject));
        return axios.post('/api/signTemplates/uploadDocTemplate', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    };

    const changeFileHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const newFile = event.target.files[0];
        if (newFile) {
            setIsFileUpdated(true);
            setAttachedFile(newFile);
            modalForm.current.setFieldValue('sourceFile', newFile.name, true);
        }
    };

    const getTemplateInput = () => {
        switch (templateType) {
            case TemplateTypes.SignRequest:
                return (
                    <>
                        <label>Sign Request Template</label>

                        <Input className="shadow-off" type="select" name="uuid">
                            <option key={''} value={undefined}></option>
                            {templates.map((template) => (
                                <option
                                    key={template.uuid}
                                    value={template.uuid}>
                                    {template.name} (signers:{' '}
                                    {template.signersCount} )
                                </option>
                            ))}
                        </Input>
                    </>
                );
            case TemplateTypes.CustomDocFile:
                return (
                    <div className="custom-doc-file-input">
                        <SelectFileInputAvaility
                            attachedFile={attachedFile}
                            onFileChange={changeFileHandler}
                            disabled={false}
                            acceptedFileTypes=".doc,.docx"
                        />
                        <div>
                            <label>Number of Signers</label>
                            <Input
                                className="shadow-off"
                                type="select"
                                name="signersCount">
                                {[1, 2].map((signersCount) => (
                                    <option
                                        key={signersCount}
                                        value={signersCount}>
                                        {signersCount}
                                    </option>
                                ))}
                            </Input>
                        </div>
                    </div>
                );
            default:
                return <></>;
        }
    };

    const renderForm = () => {
        return (
            <div>
                <Form
                    initialValues={{
                        uuid: '',
                        name: '',
                        sourceFile: '',
                        signersCount: 1,
                    }}
                    onSubmit={(fields) => saveTemplate(fields)}
                    validationSchema={yup.object().shape({
                        uuid: yup.string().when((_, schema) => {
                            return templateType === TemplateTypes.SignRequest
                                ? schema.required()
                                : schema.notRequired();
                        }),
                        sourceFile: yup.string().when((_, schema) => {
                            return templateType === TemplateTypes.CustomDocFile
                                ? schema.required()
                                : schema.notRequired();
                        }),
                        name: yup.string().required(),
                        signersCount: yup.number().required().min(1).max(2),
                    })}
                    innerRef={modalForm}>
                    <FormGroup className="template-inputs-form-group">
                        <RadioGroup
                            className="template-base-type-selector"
                            value={templateType}
                            onChange={(event) =>
                                setTemplateType(Number(event.target.value))
                            }>
                            <Row>
                                <Col md={6}>
                                    <FormControlLabel
                                        value={TemplateTypes.SignRequest}
                                        control={
                                            <Radio
                                                disableRipple={true}
                                                size="small"
                                            />
                                        }
                                        label={'Sign Request Template'}
                                    />{' '}
                                </Col>
                                <Col md={6}>
                                    <FormControlLabel
                                        value={TemplateTypes.CustomDocFile}
                                        control={
                                            <Radio
                                                disableRipple={true}
                                                size="small"
                                            />
                                        }
                                        label={'Word Document Template'}
                                    />
                                </Col>
                            </Row>
                        </RadioGroup>
                        <Row>
                            <Col md={12}>{getTemplateInput()}</Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <label>Template Name</label>
                                <Field
                                    name="name"
                                    type="text"
                                    className="form-control"
                                />
                            </Col>
                        </Row>
                    </FormGroup>
                </Form>
                <div className="footer">
                    <Button
                        className="cancel btn btn-secondary"
                        onClick={() => props.onClose()}>
                        Cancel
                    </Button>
                    <ButtonLoader
                        type="submit"
                        buttonText={'Save'}
                        disabled={false}
                        isLoading={isSaving}
                        onClick={() => {
                            modalForm.current.handleSubmit();
                        }}
                        className={'btn btn-primary loader-submit'}
                        loaderButtonText={''}></ButtonLoader>
                </div>
            </div>
        );
    };

    return props.isOpened ? (
        <div className="add-document-template-modal">
            <Rodal
                width={500}
                height={500}
                visible={props.isOpened}
                animation={'rotate'}
                onClose={() => props.onClose()}
                showMask={false}>
                <ModalHeader>Add Document Template</ModalHeader>
                <ModalBody>
                    {isLoading ? (
                        <div className="loader">{Helpers.renderLoader()}</div>
                    ) : (
                        renderForm()
                    )}
                </ModalBody>
            </Rodal>
        </div>
    ) : (
        <></>
    );
};
